import { useEffect, useRef, useState } from "react";
import { Wand2, Upload, Check, Sparkles, RefreshCw, Image as ImageIcon, AlertCircle } from "lucide-react";
import DashboardShell from "@/components/hola/DashboardShell";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";

const STYLES = [
  { k: "juvenil",  label: "Juvenil",  desc: "Vibrante Gen-Z · emojis · rosa/amarillo/cyan", swatch: ["#FF007A", "#FFE500"] },
  { k: "colorido", label: "Colorido", desc: "Gradientes festivos multi-color", swatch: ["#00C875", "#0055FF"] },
  { k: "sobrio",   label: "Sobrio",   desc: "Minimalista · un solo acento", swatch: ["#0A0A0A", "#F5F5F5"] },
  { k: "elegante", label: "Elegante", desc: "Premium · dorado sobre oscuro", swatch: ["#111111", "#D4AF37"] },
];

export default function DesignPage() {
  return (
    <DashboardShell active="design">
      {({ merchant, setMerchant }) => <DesignBody merchant={merchant} setMerchant={setMerchant} />}
    </DashboardShell>
  );
}

function DesignBody({ merchant, setMerchant }) {
  const [offers, setOffers] = useState([]);
  const [offerId, setOfferId] = useState("");
  const [style, setStyle] = useState("juvenil");
  const [hint, setHint] = useState("");
  const [spec, setSpec] = useState(null);
  const [imageDataUri, setImageDataUri] = useState(null);
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [o, q] = await Promise.all([
        api.get("/merchants/me/offers"),
        api.get("/merchants/me/design/quota"),
      ]);
      setOffers(o.data);
      if (!offerId && o.data.length > 0) setOfferId(o.data[0].offer_id);
      setQuota(q.data);
    } catch (e) { console.warn("load design err", e); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- carga al montar / al cambiar filtros

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 300_000) {
      toast.error("Máximo 300 KB. Comprime tu logo en tinypng.com");
      return;
    }
    setLogoUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await api.post("/merchants/me/logo", { data_uri: reader.result });
        setMerchant?.({ ...merchant, logo_url: data.logo_url });
        toast.success("Logo actualizado");
      } catch (e) {
        toast.error(e.response?.data?.detail || "No se pudo subir");
      } finally {
        setLogoUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const generate = async () => {
    if (!offerId) { toast.error("Selecciona una oferta"); return; }
    if (quota && quota.remaining <= 0) {
      toast.error(`Ya usaste tus ${quota.limit} rediseños del mes`);
      return;
    }
    setLoading(true);
    setSpec(null);
    setImageDataUri(null);
    try {
      const { data } = await api.post("/merchants/me/design/generate", {
        offer_id: offerId, style, extra_hint: hint,
      });
      setSpec(data.design_spec);
      setImageDataUri(data.image_data_uri || null);
      setQuota({ used_this_month: data.used_this_month, limit: data.limit, remaining: data.remaining });
      toast.success(data.image_data_uri ? "Diseño + arte IA generados. Revísalos y autoriza." : "Diseño generado (sin arte). Revísalo y autoriza.");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "object" && detail?.error === "design_quota_exhausted") {
        toast.error(detail.message);
      } else {
        toast.error(detail || "Error generando diseño");
      }
    } finally { setLoading(false); }
  };

  const authorize = async () => {
    setSaving(true);
    try {
      await api.post("/merchants/me/design/authorize", { offer_id: offerId, design_spec: spec, style, image_data_uri: imageDataUri });
      toast.success("¡Diseño autorizado! Los usuarios ya lo verán.");
      setSpec(null);
      setImageDataUri(null);
    } catch (e) {
      toast.error("No se pudo autorizar");
    } finally { setSaving(false); }
  };

  const currentOffer = offers.find((o) => o.offer_id === offerId);

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <p className="text-xs uppercase tracking-widest text-neutral-500 font-bold">Estudio de diseño</p>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">Diseña tu mensaje con IA</h1>
        <p className="text-sm text-neutral-500 mt-1">La IA genera un diseño único para tu oferta. Elige estilo, revisa y autoriza. <strong className="text-black">Máximo 4 rediseños por mes.</strong></p>
      </div>

      {/* Logo uploader */}
      <div className="rounded-2xl bg-white border border-neutral-200 p-6 flex items-center gap-4">
        <div className="w-20 h-20 rounded-2xl bg-[#FAFAFA] border border-neutral-200 grid place-items-center overflow-hidden shrink-0">
          {merchant.logo_url ? (
            <img src={merchant.logo_url} alt="logo" className="w-full h-full object-cover" data-testid="merchant-logo-preview" />
          ) : (
            <ImageIcon className="w-8 h-8 text-neutral-300" />
          )}
        </div>
        <div className="flex-1">
          <div className="font-heading font-extrabold text-lg">Logo del comercio</div>
          <p className="text-xs text-neutral-500">PNG/JPG hasta 300 KB. Aparece en tus mensajes AI.</p>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="logo-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={logoUploading} variant="outline" size="sm" className="mt-3 rounded-full" data-testid="logo-upload-btn">
            <Upload className="w-3.5 h-3.5" /> {logoUploading ? "Subiendo..." : (merchant.logo_url ? "Cambiar logo" : "Subir logo")}
          </Button>
        </div>
      </div>

      {/* Quota banner */}
      {quota && (
        <div className={`rounded-xl border p-3 flex items-center gap-3 text-sm ${quota.remaining <= 1 ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-blue-50 border-blue-200 text-blue-900"}`} data-testid="design-quota-banner">
          <AlertCircle className="w-4 h-4" />
          <span>Rediseños este mes: <strong>{quota.used_this_month}/{quota.limit}</strong> · Quedan <strong>{quota.remaining}</strong>.</span>
        </div>
      )}

      {/* Config form */}
      <div className="rounded-2xl bg-white border border-neutral-200 p-6 space-y-5">
        <div>
          <Label>Oferta a diseñar</Label>
          <select value={offerId} onChange={(e) => setOfferId(e.target.value)} className="mt-1.5 w-full h-11 rounded-xl border border-neutral-200 px-3 text-sm bg-white" data-testid="design-offer-select">
            {offers.map((o) => (<option key={o.offer_id} value={o.offer_id}>{`[${o.kind}] ${o.title}`}</option>))}
            {offers.length === 0 && <option value="">Crea primero una oferta en "Ofertas"</option>}
          </select>
        </div>

        <div>
          <Label>Estilo del mensaje</Label>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3">
            {STYLES.map((s) => (
              <button
                key={s.k}
                onClick={() => setStyle(s.k)}
                data-testid={`design-style-${s.k}`}
                className={`text-left p-4 rounded-2xl border-2 transition-all ${
                  style === s.k ? "border-[#FF007A] shadow-md" : "border-neutral-200 hover:border-neutral-300"
                }`}
              >
                <div className="flex gap-1 mb-2">
                  {s.swatch.map((c) => (<div key={c} className="w-6 h-6 rounded-full" style={{ background: c }} />))}
                </div>
                <div className="font-heading font-extrabold">{s.label}</div>
                <div className="text-[11px] text-neutral-500 mt-0.5 leading-snug">{s.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label>Instrucción extra (opcional)</Label>
          <Textarea value={hint} onChange={(e) => setHint(e.target.value)} placeholder="Ej: enfatiza el café de barista, urgencia hasta las 11am" rows={2} className="mt-1.5 rounded-xl" data-testid="design-hint-input" />
        </div>

        <Button onClick={generate} disabled={loading || !offerId || (quota && quota.remaining <= 0)} className="w-full h-12 rounded-full bg-black text-white font-bold" data-testid="design-generate-btn">
          <Wand2 className="w-4 h-4" /> {loading ? "Diseñando con IA..." : "Generar diseño"}
        </Button>
      </div>

      {/* Preview */}
      {spec && currentOffer && (
        <div className="rounded-2xl bg-white border-2 border-[#FF007A]/30 p-6" data-testid="design-preview-section">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-[#FF007A]" />
            <div className="font-heading font-extrabold text-lg">Vista previa · {style}</div>
          </div>
          <div className="max-w-sm mx-auto">
            <DesignCard spec={spec} logoUrl={merchant.logo_url} businessName={merchant.business_name} imageUrl={imageDataUri} />
          </div>
          {spec.vibe_note && (
            <div className="mt-4 text-xs text-neutral-500 italic text-center">💡 {spec.vibe_note}</div>
          )}
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Button onClick={generate} disabled={loading} variant="outline" className="rounded-full font-semibold" data-testid="design-regenerate-btn">
              <RefreshCw className="w-4 h-4" /> Volver a generar
            </Button>
            <Button onClick={authorize} disabled={saving} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-8" data-testid="design-authorize-btn">
              <Check className="w-4 h-4" /> {saving ? "Guardando..." : "AUTORIZAR DISEÑO"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function DesignCard({ spec, logoUrl, businessName, imageUrl }) {
  const bg = `linear-gradient(135deg, ${spec.gradient_from || "#FF007A"} 0%, ${spec.gradient_to || "#0055FF"} 100%)`;
  return (
    <div
      className="rounded-3xl p-6 shadow-2xl relative overflow-hidden"
      style={{ background: bg, color: spec.text_color || "#FFFFFF" }}
    >
      {/* AI-generated background image (Nano Banana). Under the gradient with mix-blend + opacity for a poster look */}
      {imageUrl && (
        <img
          src={imageUrl}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-70 mix-blend-overlay pointer-events-none"
        />
      )}
      {/* Vignette for text legibility */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 50% 100%, rgba(0,0,0,0.35), transparent 60%)" }} />
      <div className="absolute -top-16 -right-10 w-40 h-40 rounded-full bg-white/15 blur-3xl" />
      <div className="flex items-center gap-2 mb-4 relative">
        {logoUrl && <img src={logoUrl} alt="logo" className="w-9 h-9 rounded-xl object-cover bg-white/30" />}
        <div className="font-heading font-extrabold text-sm opacity-90 drop-shadow">{businessName}</div>
        {spec.badge_text && (
          <span className="ml-auto text-[11px] font-black px-2.5 py-1 rounded-full" style={{ background: spec.accent_color || "#FFFFFF", color: spec.gradient_from || "#000" }}>
            {spec.badge_text}
          </span>
        )}
      </div>
      <div className="text-4xl mb-2 relative drop-shadow">{spec.emoji}</div>
      <div className="font-heading font-black text-2xl leading-tight tracking-tight relative drop-shadow-lg">{spec.headline}</div>
      <div className="mt-2 text-sm opacity-95 relative drop-shadow">{spec.subheadline}</div>
    </div>
  );
}
